import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Text, Popover, ActionBar, useViewport } from "@bookingcom/bui-react";
import { CalendarIcon, CalendarDatesIcon } from "@bookingcom/bui-assets-react/streamline";
import { ContentWithTitle, ShellButton } from "../Shell/index.js";
import { Calendar, CalendarSelect } from "../Calendar/index.js";
import { t } from "@bookingcom/lingojs-core";
import useDate from "./useDate.js";
import styles from "./DateDropDown.module.css";
import FareCalendarLoadingAnnouncement from "../FareCalendar/FareCalendarLoadingAnnouncement.js";
import { useFareCalendarEnabled } from "../FareCalendar/useFareCalendar.js";
const errorId = "date_input_error";
const DateDropDown = (props) => {
    const { segmentIndex, position = "bottom-end", errorVariant = "tooltip", isRebook = false, forceMobileReflow = false } = props;
    const [showDropdown, setShowDropdown] = useState(false);
    const [fromKeayboard, setFromKeayboard] = useState(false);
    const buttonRef = useRef(null);
    const [selectedDate, setSelectedDate] = useState({
        from: undefined,
        to: undefined
    });
    const { errorMessage, getTextNights, i18n, isDateSelected, isRoundTrip, isMultiStop, setFromTo, text, trackV2, from, to, onTrackSBExperiment, searchType, isMobile, isDropdownOpen, fareCalendarConfig, trackCalendarOpenStage, isCalendarReflowFixEnabled } = useDate({ segmentIndex });
    const { isLarge } = useViewport();
    const reflowAtSmall = (isCalendarReflowFixEnabled || forceMobileReflow) && !isLarge;
    const isFareCalendarEnabled = useFareCalendarEnabled(segmentIndex, fareCalendarConfig, "desktop");
    const isFareCalendarDisplayEnabled = isFareCalendarEnabled && !!fareCalendarConfig.isFareCalendarDisplayEnabled;
    const popoverClassName = useMemo(() => {
        const isFareCalendar = isFareCalendarDisplayEnabled;
        const isReflow = isCalendarReflowFixEnabled || reflowAtSmall;
        if (isFareCalendar && isReflow)
            return styles.popovercontainerFareCalendarReflow;
        if (isFareCalendar)
            return styles.popovercontainerFareCalendar;
        if (isReflow)
            return styles.popovercontainerReflow;
        return styles.popovercontainer;
    }, [isFareCalendarDisplayEnabled, isCalendarReflowFixEnabled, reflowAtSmall]);
    useEffect(() => {
        setTimeout(() => {
            setShowDropdown(isDropdownOpen || false);
        }, 0);
    }, [isDropdownOpen]);
    useEffect(() => {
        if (showDropdown) {
            trackCalendarOpenStage();
        }
    }, [showDropdown, trackCalendarOpenStage]);
    const handleOnClose = useCallback(() => {
        setShowDropdown(false);
        setFromKeayboard(false);
    }, []);
    const handleOnSelectDateChange = useCallback(({ from, to }) => {
        setSelectedDate({ from, to });
    }, []);
    const handleOnDoneClick = useCallback(() => {
        setFromTo({ from: selectedDate.from, to: selectedDate.to });
        trackV2("click_calendar_done");
        handleOnClose();
    }, [setFromTo, selectedDate.from, selectedDate.to, trackV2, handleOnClose]);
    const handleOnDateChange = useCallback((data) => {
        const { from, to, offerAccuracyTrackerId } = data;
        setFromTo({ from, to, offerAccuracyTrackerId });
        if (isRoundTrip && from && to) {
            handleOnClose();
        }
        else if (!isRoundTrip && from) {
            setFromTo({ from, offerAccuracyTrackerId });
            handleOnClose();
        }
    }, [setFromTo, isRoundTrip, handleOnClose]);
    const handleOnClick = useCallback((e) => {
        e?.stopPropagation();
        const isFromKeyboard = e.nativeEvent.isTrusted && e.nativeEvent.detail === 0 && !e.nativeEvent["pointerType"];
        setFromKeayboard(isFromKeyboard);
        trackV2("click_calendar");
        setTimeout(() => setShowDropdown(true), 0);
    }, [from, isMultiStop, isRoundTrip, onTrackSBExperiment, to, trackV2, isMobile]);
    const titleValue = useMemo(() => {
        if (isRebook) {
            return searchType === "ROUNDTRIP"
                ? i18n.trans(t("flights_pb_rebook_calendar_prompt_rt"))
                : i18n.trans(t("flights_pb_rebook_calendar_prompt_ow"));
        }
        else {
            return searchType === "ROUNDTRIP"
                ? i18n.trans(t("flights_searchbox_travel_dates"))
                : i18n.trans(t("flights_searchbox_travel_date"));
        }
    }, [isRebook, searchType, i18n]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ShellButton, { icon: isDateSelected ? CalendarDatesIcon : CalendarIcon, errorMessage: showDropdown ? undefined : errorMessage, errorVariant: errorVariant, errorPosition: "bottom", ref: buttonRef, "data-ui-name": `button_date_segment_${segmentIndex}`, onClick: handleOnClick, "data-ui-error": Boolean(errorMessage), errorId: errorId, halfVerticalPadding: true, alignIcon: "center", "aria-invalid": !!errorMessage, "aria-describedby": searchType === "MULTISTOP" ? `a11y_multi_segment_description_${segmentIndex}` : undefined },
            React.createElement(ContentWithTitle, { title: titleValue, content: text })),
        React.createElement(Popover, { fill: true, hideClose: true, forcePosition: false, size: "auto", triggerRef: buttonRef, active: showDropdown, onClose: handleOnClose, trapFocusMode: "hard", position: reflowAtSmall ? "bottom" : position, disableAnimation: true, keepMounted: false },
            React.createElement(Popover.Content, null,
                React.createElement(FareCalendarLoadingAnnouncement, { segmentIndex: segmentIndex, fareCalendarConfig: fareCalendarConfig, mode: "desktop" }),
                React.createElement("div", { className: popoverClassName }, fromKeayboard ? (React.createElement("div", { className: styles.calendarWrapper },
                    React.createElement("div", { className: styles.calendarWrapperInner },
                        React.createElement(CalendarSelect, { variant: "horizontal", segmentIndex: segmentIndex, onDateChange: handleOnSelectDateChange })),
                    React.createElement(ActionBar, { button: {
                            wide: true,
                            onClick: handleOnDoneClick,
                            text: i18n.trans(t("flights_action_done")),
                            attributes: {
                                "data-ui-name": "button_calendar_action_bar_done"
                            }
                        } },
                        React.createElement(Text, { variant: "body_2" }, getTextNights(selectedDate))))) : (React.createElement("div", { className: styles.calendarWrapperInner },
                    React.createElement(Calendar, { fullHeight: true, mode: reflowAtSmall ? "single" : "double", onDateChange: handleOnDateChange, segmentIndex: segmentIndex }))))))));
};
export default DateDropDown;
